import React, { useEffect, useState } from 'react';
import { AppColors } from '../../Utils/helper/AppColors';
import {
	Avatar,
	Card,
	Divider,
	Grid,
	IconButton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Button,
	Box,
} from '@mui/material';
import { BackIcon, CloseIcon, CopyIcon } from '../Icons/Icons';
import { copyToClipboard, getInitials } from '../../Utils/helper/commonUtils';
import CultService from '../../Services/CultService';
import moment from 'moment';
import CircularProgress from '../../Screens/Dashboard/Components/CircularProgress';
import { HelperTexts } from '../../Utils/helper/HelperTexts';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import DotChip from '../DotChip';
import CallRecordingsList from '../../Screens/Recordings/Components/CallRecordingsList';

const style = {
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
	bgcolor: AppColors.WHITE,
	borderRadius: 3,
};

const DetailsItem = ({ label, value, copy, expandable = false, maxLength = 50 }) => {
	const [showMore, setShowMore] = useState(false);

	const isLongText = value?.length > maxLength;
	const displayValue = !expandable || !isLongText ? value : showMore ? value : value.slice(0, maxLength) + '...';

	return (
		<Card noShadow sx={{ p: 1.5, py: 1 }}>
			<Stack width="100%" spacing={0.5}>
				<Typography variant="subtitle2" fontSize={11}>
					{label}
				</Typography>

				<Typography variant="subtitle1">{displayValue}</Typography>

				<Stack direction="row" justifyContent="space-between" alignItems="center">
					{/* Empty placeholder to push Show More to right */}
					<Box />

					<Stack direction="row" alignItems="center" gap={1}>
						{expandable && isLongText && (
							<Button
								onClick={() => setShowMore((prev) => !prev)}
								size="small"
								sx={{ p: 0.3, px: 1, fontSize: 11, minWidth: 'fit-content', textTransform: 'none' }}
							>
								{showMore ? 'Show Less' : 'Show More'}
							</Button>
						)}

						{copy && (
							<IconButton onClick={() => copyToClipboard(value)} size="small">
								<CopyIcon />
							</IconButton>
						)}
					</Stack>
				</Stack>
			</Stack>
		</Card>
	);
};

export default function UserDetails({ userId, onClose, type , noPagination  }) {
	const [details, setDetails] = useState({});
	const [issues, setIssues] = useState([]);
	const [recordings, setRecordings] = useState([]);
	const [noteDetails, setNotedDetails] = useState({});
	const [pagination, serPagination] = useState({ page: 1, rowsPerPage: 10, count: 0 });

	const company = useSelector((state) => state?.app?.company);

	const getUserDetails = async () => {
		let res = await CultService.getUserById({ params: { id: userId } });
		if (res.status == 200) {
			setDetails(res?.data?.data);
		}
	};

	const getIssues = async () => {
		try {
			let res = await CultService.getCallIssues({
				params: {
					companyId: company?._id,
					users: [userId],
				},
			});
			setIssues(res?.data?.data?.issues);
		} catch (err) {
			toast.error(HelperTexts?.SOMETHING_WENT_WRONG);
		} finally {
		}
	};

	const getRecordings = async (page = 1, limit = pagination?.rowsPerPage) => {
		try {
			let res = await CultService.getCallRecordings({
				params: {
					companyId: company?._id,
					users: [userId],
					...(noPagination ? {} : { page: pagination.page, limit: pagination.rowsPerPage }),

				},
			});
			setRecordings(res?.data?.data?.recordings);
			serPagination({
				page: res?.data?.data?.currentPage,
				count: res?.data?.data?.totalRecords,
				rowsPerPage: limit,
				totalPages: res?.data?.data?.totalPages,
			});
		} catch (err) {
			toast.error(HelperTexts?.SOMETHING_WENT_WRONG);
		} finally {
		}
	};

	const getUserRecordings = async (page = 1, limit = pagination?.rowsPerPage) => {
		try {
			let res = await CultService.getUserCallRecordings({
				params: { page: page, limit: limit, companyId: company?._id, users: [userId] },
			});
			setRecordings(res?.data?.data?.recordings);
			serPagination({
				page: res?.data?.data?.currentPage,
				count: res?.data?.data?.totalRecords,
				rowsPerPage: limit,
				totalPages: res?.data?.data?.totalPages,
			});
		} catch (err) {
			toast.error(HelperTexts?.SOMETHING_WENT_WRONG);
		} finally {
		}
	};

	const getNoteDetails = async () => {
		try {
			let res = await CultService.getUserNotes({ params: { userId } });
			setNotedDetails(res?.data);
		} catch (err) {
			toast.error(HelperTexts?.SOMETHING_WENT_WRONG);
		} finally {
		}
	};

	useEffect(() => {
		if (company) {
			getIssues();
			getUserDetails();
			getNoteDetails();
		}
	}, [company]);

	useEffect(() => {
		if (!company) return;
		if (type === 'criticalSectionUser') {
			getUserRecordings(pagination?.page, pagination?.rowsPerPage);
		} else {
			getRecordings();
		}
	}, []);

	// console.log(noteDetails);

	return (
		<Stack sx={style} width="65vw">
			<Stack p={2} direction="row" alignItems="center" gap={1}>
				<IconButton onClick={onClose} size="small">
					<BackIcon />
				</IconButton>
				{details?.image ? (
					<Avatar
						sx={{ height: 34, width: 34 }}
						src="https://img.freepik.com/free-photo/portrait-young-woman-with-natural-make-up_23-2149084942.jpg"
					/>
				) : (
					<Stack
						flexShrink={0}
						height={34}
						width={34}
						borderRadius={50}
						bgcolor={AppColors.BORDER_GREY}
						border="1px solid"
						borderColor="#dee1e3"
						alignItems="center"
						justifyContent="center"
						sx={{ cursor: 'pointer', transition: 'all .2s', ':hover': { bgcolor: '#e4e6eb' } }}
					>
						<Typography variant="subtitle2">{getInitials({ user: details })}</Typography>
					</Stack>
				)}
				<Typography variant="subtitle2" fontWeight={500}>
					{details?.anonymous ? 'Anonymous' : `${details?.firstName || ''} ${details?.lastName || ''}`}
				</Typography>
				<IconButton onClick={onClose} sx={{ ml: 'auto' }} size="small">
					<CloseIcon />
				</IconButton>
			</Stack>
			<Divider />

			<Stack maxHeight="calc(100vh - 120px)" overflow="auto" p={2} gap={2}>
				<Stack gap={1}>
					<Typography ml={1.5} variant="body1">
						Basic Information
					</Typography>
					<Card noShadow>
						<Stack width="100%" direction="row" gap={2} justifyContent="space-between">
							{details?.image ? (
								<Avatar
									sx={{ height: 200, width: 200, borderRadius: 3 }}
									src="https://img.freepik.com/free-photo/portrait-young-woman-with-natural-make-up_23-2149084942.jpg"
								/>
							) : (
								<Stack
									flexShrink={0}
									height={200}
									width={200}
									borderRadius={3}
									bgcolor={AppColors.BORDER_GREY}
									border="1px solid"
									borderColor="#dee1e3"
									alignItems="center"
									justifyContent="center"
								>
									<Typography variant="subtitle2" fontSize={50}>
										{getInitials({ user: details })}
									</Typography>
								</Stack>
							)}

							<Grid container spacing={2}>
								<Grid item xs={6}>
									<DetailsItem label="First Name" value={details?.firstName} />
								</Grid>
								<Grid item xs={6}>
									<DetailsItem label="Last Name" value={details?.lastName} />
								</Grid>
								<Grid item xs={12}>
									<DetailsItem label="Role" value={details?.role} expandable />
								</Grid>
								<Grid item xs={6}>
									<DetailsItem label="Location" value={details?.country} />
								</Grid>
								<Grid item xs={6}>
									<DetailsItem label="Email Address" value={details?.email} copy />
								</Grid>
								<Grid item xs={6}>
									<DetailsItem label="Phone Number" value={details?.phoneNumber} copy />
								</Grid>
							</Grid>
						</Stack>
					</Card>
				</Stack>

				<Stack direction="row" gap={2}>
					<Card noShadow sx={{ width: 180, height: 'fit-content' }}>
						<Stack gap={1} width="100%" alignItems="center" justifyContent="center">
							<CircularProgress hideBorders size={80} value={parseInt(+noteDetails?.participationRate)} color="#7F56D9" />
							<Typography textAlign="center" variant="subtitle2">
								Participation Rate
							</Typography>
						</Stack>
					</Card>
					<Card sx={{ p: 0 }} noShadow>
						<Stack p={1.5} width="100%" direction="row" alignItems="center" justifyContent="space-between">
							<Typography variant="body1">Notes</Typography>
							{/* <AddIcon /> */}
						</Stack>

						<Divider sx={{ width: '100%' }} />

						<Stack width="100%" divider={<Divider />}>
							{noteDetails?.comments?.length > 0 ? (
								noteDetails?.comments?.map((i) => (
									<Stack key={i.id} p={1.5} direction="row" alignItems="flex-start" gap={2}>
										hey
										<Typography variant="subtitle2">{moment(i?.createdAt).format('ll')}</Typography>
										<Typography variant="subtitle2">{i?.text}</Typography>
										{/* <Stack ml="auto" direction="row" alignItems="center" gap={2}>
										<CheckIcon color="#667085" />
										<DeleteIcon color="#667085" />
									</Stack> */}
									</Stack>
								))
							) : (
								<Typography variant="subtitle2" p={2}>
									No notes added yet!
								</Typography>
							)}
						</Stack>
					</Card>
				</Stack>

				<Stack gap={1}>
					<Typography ml={1.5} variant="body1">
						Issues
					</Typography>
					<TableContainer sx={{ minHeight: 'fit-content' }}>
						<Table stickyHeader>
							<TableHead sx={{ '& .MuiTableCell-root': { fontSize: 12, color: AppColors.TEXT_TERTIARY, fontWeight: 500 } }}>
								<TableCell>Issue</TableCell>
								<TableCell>Category</TableCell>
								<TableCell width={200}>Department</TableCell>
							</TableHead>
							<TableBody>
								{issues?.map((c, index) => (
									<TableRow key={index}>
										<TableCell>
											<Typography sx={{ textTransform: 'capitalize' }} variant="subtitle2">
												{c?.callIssues?.issues?.join(', ')}
											</Typography>
										</TableCell>
										<TableCell>
											{c?.callIssues?.category && <DotChip color={AppColors.TEXT_SUCCESS} label={c?.callIssues?.category} />}
										</TableCell>
										<TableCell>
											<Typography textTransform="capitalize" variant="subtitle2">
												{c?.department?.toLowerCase()}
											</Typography>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Stack>

				<Stack gap={1}>
					<Typography ml={1.5} variant="body1">
						Conversations
					</Typography>
					<CallRecordingsList
						hideUser
						height="fit-content"
						fetch={type === 'criticalSectionUser' ? getUserRecordings : getRecordings}
						recordings={recordings}
						pagination={pagination}
						noPagination = {noPagination}
					/>
				</Stack>
			</Stack>
		</Stack>
	);
}
